import { CategoryTile } from '@/components/CategoryTile';
import type { CategoryMeta } from '@/components/transactions/screen/categoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { SpaceCategorySpend } from '@/lib/spaces/categories';
import type { BudgetProgress } from '@/lib/spaces/home';
import { cn } from '@/lib/utils';

// The tone compares the amounts (`budgetProgress`), never the rounded percentage.
export function SpaceBudgetBar({ label, progress, className }: Readonly<{ label: string; progress: BudgetProgress | null; className: string }>) {
  if (!progress) return null;
  return <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.width} aria-valuetext={`${progress.pct}% do limite`} className={cn('overflow-hidden rounded-full bg-surface-sunken', className)}>
    <div className={cn('h-full rounded-full', progress.over ? 'bg-error' : progress.near ? 'bg-warning' : 'bg-primary')} style={{ width: `${progress.width}%` }} />
  </div>;
}

// Whole reais, like the personal home's categories: a reading of the period, not a statement.
export function SpaceCategorySpendRow({ row, meta, nested = false }: Readonly<{ row: SpaceCategorySpend; meta: CategoryMeta; nested?: boolean }>) {
  const format = useFormatCurrency();
  const amount = format(row.spentCents / 100, { noCents: true });
  const limit = row.limitCents === null ? null : format(row.limitCents / 100, { noCents: true });
  return <div className={cn('flex items-center gap-3', nested && 'pl-12')}>
    <CategoryTile meta={meta} size="sm" className={nested ? 'size-7 rounded-lg' : undefined} />
    <div className="min-w-0 flex-1 space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className={cn('min-w-0 break-words text-text-primary', nested ? 'text-xs' : 'text-sm font-medium')}>{row.label}</span>
        <span className={cn('font-mono text-xs tabular-nums', row.progress?.over ? 'text-error-text' : 'text-text-secondary')}>
          <span className="font-semibold text-text-primary">{amount}</span>{limit ? ` de ${limit}` : ' · sem limite'}
        </span>
      </div>
      <SpaceBudgetBar label={`Limite de ${row.label} usado`} progress={row.progress} className={nested ? 'h-1' : 'h-1.5'} />
    </div>
  </div>;
}
